import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface TeamMember {
  id: string;
  name: string;
  roleKey: string;
  image: string;
  bioKey: string;
  tagKeys: string;
  cropBottom?: boolean;
  placeholder: boolean;
}

interface TeamMemberCardProps {
  member: TeamMember;
  index: number;
  isVisible: boolean;
}

export default function TeamMemberCard({ member, index, isVisible }: TeamMemberCardProps) {
  const { t } = useTranslation();
  const [flipped, setFlipped] = useState(false);
  const revealClass = isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6';
  const delay = `${index * 90}ms`;

  const role = t(member.roleKey);
  const bio = t(member.bioKey);
  const tags = t(member.tagKeys, { returnObjects: true }) as string[];

  if (member.placeholder) {
    return (
      <div
        className={`relative flex flex-col items-center justify-center h-[220px] sm:h-[230px] lg:h-[240px] rounded-lg border border-dashed border-background-300/80 bg-background-100/60 p-4 text-center transition-all duration-700 ${revealClass}`}
        style={{ transitionDelay: delay }}
      >
        <div className="w-10 h-10 rounded-full bg-background-200/70 flex items-center justify-center mb-2">
          <i className="ri-user-add-line text-base text-foreground-400" />
        </div>
        <span className="text-[10px] font-mono uppercase tracking-widest text-foreground-500">
          {t('about.team.comingSoon')}
        </span>
        <span className="text-[10px] text-foreground-400 mt-1 max-w-[170px] leading-relaxed">
          {t('about.team.comingSoonText')}
        </span>
      </div>
    );
  }

  const handleToggle = () => setFlipped((value) => !value);

  return (
    <div
      className={`flip-card h-[220px] sm:h-[230px] lg:h-[240px] transition-all duration-700 ${flipped ? 'is-flipped' : ''} ${revealClass}`}
      style={{ transitionDelay: delay }}
      role="button"
      tabIndex={0}
      aria-label={`${member.name} — ${role}`}
      onClick={handleToggle}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleToggle();
        }
      }}
      onFocus={() => setFlipped(true)}
      onBlur={() => setFlipped(false)}
    >
      <div className="flip-card-inner cursor-pointer">
        {/* Front — full photo, name, role */}
        <div className="flip-card-face bg-background-50">
          <div
            className={`absolute inset-x-0 top-0 bottom-16 flex justify-center p-2 overflow-hidden ${
              member.cropBottom ? 'items-start' : 'items-center'
            }`}
          >
            <img
              src={member.image}
              alt={`${member.name} — ${role} — MorphenIQ`}
              title={`${member.name} ${role}`}
              className={
                member.cropBottom
                  ? 'h-[120%] w-auto max-w-none max-h-none object-contain object-top'
                  : 'max-w-full max-h-full w-auto h-auto object-contain'
              }
            />
          </div>
          <div className="absolute inset-x-0 bottom-0 h-16 bg-primary-500 flex flex-col items-center justify-center px-3 text-center">
            <h3 className="text-xs md:text-[13px] font-heading font-semibold text-background-50 leading-snug">
              {member.name}
            </h3>
            <p className="text-[10px] font-mono uppercase tracking-wide text-secondary-300 mt-0.5">
              {role}
            </p>
          </div>
          <span className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-background-50/85 border border-background-200 flex items-center justify-center text-foreground-700">
            <i className="ri-loop-right-line text-xs" />
          </span>
        </div>

        {/* Back — full profile */}
        <div className="flip-card-face flip-card-back bg-primary-500">
          <div className="w-full h-full overflow-y-auto p-3.5 flex flex-col">
            <span className="inline-block w-fit px-2 py-0.5 rounded-full bg-accent-500/25 border border-accent-300/40 text-[8px] font-mono font-medium text-accent-200 uppercase tracking-widest mb-2">
              {role}
            </span>
            <h3 className="text-sm font-heading font-semibold leading-snug bg-gradient-to-r from-secondary-200 via-background-50 to-accent-200 bg-clip-text text-transparent">
              {member.name}
            </h3>
            <p className="text-[11px] text-background-50/85 leading-relaxed mt-2">
              {bio}
            </p>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 rounded-full bg-secondary-500/15 border border-secondary-300/30 text-[9px] font-medium text-secondary-200 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}